import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from './axiosBaseQuery';
import { CACHE_DURATIONS } from './baseApi';
import type {
  ContactSearchApiResponse,
  NegativeSearchContact,
  NegativeSearchResponse,
} from '@/lib/types/people.types';
import { normalizeSelectionIds } from '@/lib/utils/selection-ids.utils';

/**
 * People (contact) search API.
 *
 * Every endpoint here talks to the standalone contact search service through
 * the Express proxy at `/api/contact-search` — see
 * packages/server/api/contact-search. The suppression-list, saved-contact,
 * dropdown and radar endpoints this slice used to carry are gone: they rode a
 * mock base query with no backend behind them and have real homes now
 * (`listApi`, `savedContactsApi`, `dropdownApi`), so nothing imported them.
 */

/** The search tag, invalidated by bulk-batch-completion.utils when a background unlock finishes. */
export const NEGATIVE_SEARCH_TAG = 'NegativeSearch';

/**
 * The slice of the store this API reads the signed-in user from. Declared
 * locally rather than imported as `RootState`, because store.ts imports this
 * module to register the reducer — taking its type back would be a cycle.
 */
interface AuthAwareState {
  auth?: { currentUser?: { _id?: string } | null } | null;
}

function readUserId(getState: () => unknown): string {
  return (getState() as AuthAwareState)?.auth?.currentUser?._id || '';
}

/**
 * Envelope shapes the contact search service has been seen to answer with.
 * `results` is the confirmed live shape (mirroring the company search
 * service's `{ total, from, size, results }`); the rest are kept as fallbacks
 * for other deployments/versions of that service.
 */
interface ContactSearchEnvelope extends Partial<ContactSearchApiResponse> {
  contacts?: NegativeSearchContact[];
  data?: NegativeSearchContact[];
  count?: number;
  hits?: {
    total?: { value?: number };
    hits?: Array<{ _id: string; _source: Record<string, unknown> }>;
  };
}

/** Normalizes whatever the upstream answers with into `{ contacts, total }`. */
function normalizeContactSearchResponse(response: unknown): NegativeSearchResponse {
  const body = (response ?? {}) as ContactSearchEnvelope;

  const contacts: NegativeSearchContact[] =
    body.results ??
    body.contacts ??
    body.data ??
    body.hits?.hits?.map(
      (hit) => ({ person_id: hit._id, ...hit._source }) as unknown as NegativeSearchContact
    ) ??
    [];

  const total = body.total ?? body.count ?? body.hits?.total?.value ?? contacts.length;

  return { contacts, total };
}

/**
 * The filter body `buildPeopleSearchPayload` assembles, forwarded to the
 * service verbatim. Only the keys this slice reads itself are named; the
 * payload carries a large, UI-driven set of filter groups besides.
 */
export interface ContactSearchPayload {
  /** Company-side filters (name, HQ location, size, revenue, industry…). */
  company?: Record<string, unknown>;
  /** Contact-side filters (person name, title, seniority, location…). */
  contact?: Record<string, unknown>;
  [key: string]: unknown;
}

/**
 * Paging is sent as query parameters, not in the body, so it is split out of
 * the payload before the request goes out.
 */
export interface ContactSearchParams extends ContactSearchPayload {
  size?: number;
  from?: number;
}

/** `start` is the offset to select FROM, so "Select 100" on page 4 picks that page's 100 rows. */
export interface ContactSelectionParams {
  /** Falls back to the signed-in user when empty. */
  userId?: string;
  contact?: Record<string, unknown>;
  company?: Record<string, unknown>;
  limit?: number;
  start?: number;
}

export const peopleApi = createApi({
  reducerPath: 'peopleApi',
  baseQuery: axiosBaseQuery('/api/contact-search'),
  tagTypes: [NEGATIVE_SEARCH_TAG],
  refetchOnFocus: false,
  refetchOnReconnect: true,
  keepUnusedDataFor: CACHE_DURATIONS.SHORT,
  endpoints: (builder) => ({
    /**
     * Contact search.
     *
     * userId (from the authenticated session), size and from go as query
     * parameters; the filters go in the body. The service checks userId
     * against the DB to decide which contacts the user has unlocked access to.
     *
     * See buildPeopleSearchPayload() — userId is no longer in the body.
     */
    searchNegativeContacts: builder.query<NegativeSearchResponse, ContactSearchParams>({
      queryFn: async (payload, api, _extraOptions, baseQuery) => {
        const { size, from, ...body } = payload;

        const result = await baseQuery({
          url: '',
          method: 'POST',
          body,
          params: { userId: readUserId(api.getState), size: size ?? 25, from: from ?? 0 },
        });

        if (result.error) return { error: result.error };

        return { data: normalizeContactSearchResponse(result.data) };
      },

      keepUnusedDataFor: CACHE_DURATIONS.VERY_SHORT,

      forceRefetch({ currentArg, previousArg }) {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
      },

      providesTags: (_result, _error, params) => [
        NEGATIVE_SEARCH_TAG,
        { type: NEGATIVE_SEARCH_TAG, id: JSON.stringify(params) },
      ],
    }),

    /**
     * Contact IDs for bulk selection across pages ("Select 25/50/100/…").
     *
     * Hits the upstream's dedicated ids-only endpoint, POST
     * /api/contact-search/selection. It runs the identical query with
     * `"_source": false`, so Elasticsearch never serializes the document
     * bodies — selecting 5000 rows costs 5000 id strings instead of 5000 full
     * contact records with every field discarded but one.
     *
     * The ids ARE person_ids: the contacts index is keyed by person_id
     * (verified against the live service — selection ids match the `person_id`
     * of the same rows from /contacts/search), so they drop straight into the
     * save/unlock calls that key on person_id.
     */
    searchContactsSearchSelection: builder.query<string[], ContactSelectionParams>({
      queryFn: async ({ userId, contact, company, limit = 25, start = 0 }, api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: '/selection',
          method: 'POST',
          body: { contact, company },
          params: { userId: userId || readUserId(api.getState), size: limit, from: start },
        });

        if (result.error) return { error: result.error };

        return { data: normalizeSelectionIds(result.data) };
      },

      // 1 minute - selection data is temporary
      keepUnusedDataFor: CACHE_DURATIONS.VERY_SHORT,
    }),
  }),
});

export const {
  useLazySearchNegativeContactsQuery,
  useLazySearchContactsSearchSelectionQuery,
} = peopleApi;

export default peopleApi;
